// https://prototype.sqprod.co/#/docs/squareup.franklin.api.FormBlocker.Element.ButtonElement
import React, { MutableRefObject, useContext, useState } from 'react'

import { useFormContext } from 'react-hook-form'

import { ArcadeButton, ButtonProminence } from '@cash-design-system/react'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import {
  FormBlockerElementButtonElement,
  FormBlockerElementButtonElementStyle,
  FormBlockerElementButtonElementType,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { getBlockerAction } from '@cash-web/shared-flow-feature-main'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { WebViewUnsupportedFeature } from '@squareup/customer-data-events-web'

import { isContactSupportButton } from '../../../../util/isContactSupportButton'
import { FormBlockerContext } from '../../FormBlockerContext'

type SpaceButtonProps = { space?: string }
type Props = FormBlockerElementButtonElement & {
  disabled?: boolean
  canSubmit?: boolean
  selectedAction?: MutableRefObject<string | undefined>
} & SpaceButtonProps

const getArcadeProminence = (
  action: BlockerAction,
  style?: FormBlockerElementButtonElementStyle,
  type?: FormBlockerElementButtonElementType
): ButtonProminence => {
  // Standardize all "Contact Support" buttons to secondary variant for consistency
  if (isContactSupportButton(action.text)) {
    return 'standard'
  } else if (action.view_action?.title === 'direct-deposit-authorization-form') {
    return 'prominent'
  } else if (type === FormBlockerElementButtonElementType.DESTRUCTIVE) {
    return 'prominent'
  } else if (style === FormBlockerElementButtonElementStyle.PRIMARY) {
    return 'prominent'
  } else if (style === FormBlockerElementButtonElementStyle.SECONDARY) {
    return 'standard'
  } else if (style === FormBlockerElementButtonElementStyle.TERTIARY) {
    return 'subtle'
  }

  return 'prominent'
}

export function ButtonElement({
  action,
  style,
  type,
  disabled,
  canSubmit,
  selectedAction,
}: Props): React.ReactElement | null {
  const context = useContext(FormBlockerContext)
  const { error } = useContext(ErrorContext)
  const journey = useJourneyContext()
  const form = useFormContext()
  const logCdfEvent = useLogCashWebCdfEvent()

  const [isNonPrimaryOrSecondaryButtonLoading, setIsNonPrimaryOrSecondaryButtonLoading] = useState<boolean>(false)

  if (!action) {
    return null
  }

  const blockerAction = getBlockerAction(action, journey)
  if (!blockerAction.isSupported) {
    logCdfEvent(
      WebViewUnsupportedFeature({
        source: 'ButtonElement',
        feature: 'BLOCKER_ACTION',
        action: blockerAction.actionName,
      })
    )
    return null
  }

  const prominence = getArcadeProminence(action, style, type)
  const isLoading = canSubmit
    ? form?.formState?.isSubmitting && selectedAction?.current === prominence
    : isNonPrimaryOrSecondaryButtonLoading && !error

  return (
    <ArcadeButton
      disabled={disabled}
      isDestructive={type === FormBlockerElementButtonElementType.DESTRUCTIVE}
      isLoading={isLoading}
      prominence={prominence}
      role="button"
      size="cta"
      type={canSubmit ? 'submit' : 'button'}
      onClick={() => {
        if (selectedAction) {
          selectedAction.current =
            type === FormBlockerElementButtonElementType.DESTRUCTIVE &&
            style === FormBlockerElementButtonElementStyle.PRIMARY
              ? 'prominent'
              : prominence
        }
        if (!canSubmit) {
          if (action.submit_action) {
            setIsNonPrimaryOrSecondaryButtonLoading(true)
          }
          context.onFormBlockerAction(action)
        }
      }}
    >
      {action.text}
    </ArcadeButton>
  )
}
